import { For, Show, createResource, createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { useApi, isOk } from '../api';
import type { PlayerBlacklistEntryDto, BlacklistShutdownScope } from '../api';
import Text from '../components/Text';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { EmptyState } from '../components/EmptyState';
import { SkeletonAuditRow } from '../components/Skeleton';
import styles from './AdminPlayerBlacklist.module.css';
import { firstError } from '../api/envelope';

const SCOPE_KEYS: Record<number, string> = {
    0: 'BLACKLIST_SCOPE_SPOTTING_BOT',
    1: 'BLACKLIST_SCOPE_SAME_SERVER',
    2: 'BLACKLIST_SCOPE_ALL_BOTS',
};
const SCOPE_VALUES = Object.keys(SCOPE_KEYS).map(Number);

interface EntryForm {
    playerName: string;
    scope: number;
    triggerOnSight: boolean;
    triggerOnMessage: boolean;
    isEnabled: boolean;
    note: string;
}

const emptyForm = (): EntryForm => ({
    playerName: '',
    scope: 0,
    triggerOnSight: true,
    triggerOnMessage: true,
    isEnabled: true,
    note: '',
});

export default function AdminPlayerBlacklist() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [reloadToken, setReloadToken] = createSignal(0);
    const [form, setForm] = createStore<EntryForm>(emptyForm());
    const [editingId, setEditingId] = createSignal<string | null>(null);
    const [submitting, setSubmitting] = createSignal(false);

    const [entries] = createResource(
        reloadToken,
        async (): Promise<PlayerBlacklistEntryDto[]> => {
            try {
                const res = await api.playerBlacklistAdmin.listEntries();
                if (isOk(res.data)) return res.data.value.entries ?? [];
            } catch { /* fall through to the toast */ }
            toast?.error(t('BLACKLIST_FETCH_FAILED'));
            return [];
        }
    );

    const loading = () => entries.state === 'pending';
    const reload = () => setReloadToken((n) => n + 1);
    const scopeLabel = (scope: number | undefined) => t(SCOPE_KEYS[scope ?? -1] ?? 'BLACKLIST_SCOPE_SPOTTING_BOT');

    const resetForm = () => {
        setForm(emptyForm());
        setEditingId(null);
    };

    const startEdit = (entry: PlayerBlacklistEntryDto) => {
        setForm({
            playerName: entry.playerName ?? '',
            scope: entry.scope ?? 0,
            triggerOnSight: entry.triggerOnSight ?? true,
            triggerOnMessage: entry.triggerOnMessage ?? true,
            isEnabled: entry.isEnabled ?? true,
            note: entry.note ?? '',
        });
        setEditingId(entry.id ?? null);
    };

    const submit = async () => {
        const playerName = form.playerName.trim();
        if (!playerName) {
            toast?.error(t('BLACKLIST_NAME_REQUIRED'));
            return;
        }

        setSubmitting(true);
        try {
            const body = {
                playerName,
                scope: form.scope as BlacklistShutdownScope,
                triggerOnSight: form.triggerOnSight,
                triggerOnMessage: form.triggerOnMessage,
                isEnabled: form.isEnabled,
                note: form.note.trim() ? form.note.trim() : null,
            };

            const id = editingId();
            const res = id
                ? await api.playerBlacklistAdmin.updateEntry(id, body)
                : await api.playerBlacklistAdmin.createEntry(body);

            if (isOk(res.data)) {
                toast?.info(id ? t('BLACKLIST_UPDATED') : t('BLACKLIST_ADDED'));
                resetForm();
                reload();
            } else {
                // Keeps what was typed: the usual failure is a duplicate name worth correcting.
                toast?.error(firstError(res.data) ?? t('BLACKLIST_SAVE_FAILED'));
            }
        } catch {
            toast?.error(t('BLACKLIST_SAVE_FAILED'));
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (entry: PlayerBlacklistEntryDto) => {
        if (!entry.id) return;
        if (!await showConfirm(t('BLACKLIST_CONFIRM_DELETE', entry.playerName ?? '?'), true)) return;
        try {
            const res = await api.playerBlacklistAdmin.deleteEntry(entry.id);
            if (isOk(res.data)) {
                toast?.info(t('BLACKLIST_DELETED'));
                if (editingId() === entry.id) resetForm();
                reload();
            } else {
                toast?.error(t('BLACKLIST_DELETE_FAILED'));
            }
        } catch {
            toast?.error(t('BLACKLIST_DELETE_FAILED'));
        }
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="BLACKLIST_TITLE" /></h1>
                <span class={styles.entryCount}>
                    {(entries() ?? []).length} <Text val="BLACKLIST_ENTRIES" />
                </span>
            </div>

            <p class={styles.intro}><Text val="BLACKLIST_INTRO" /></p>

            <form class="fcard v" onSubmit={(e) => { e.preventDefault(); void submit(); }} style={{ gap: 'var(--spacing-md)' }}>
                <h2 style={{ margin: 0 }}>
                    <Text val={editingId() ? 'BLACKLIST_EDIT_ENTRY' : 'BLACKLIST_ADD_ENTRY'} />
                </h2>

                <div class={styles.formGrid}>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="BLACKLIST_FIELD_NAME" />
                        </span>
                        <input
                            type="text"
                            value={form.playerName}
                            onInput={(e) => setForm('playerName', e.currentTarget.value)}
                            placeholder={t('BLACKLIST_FIELD_NAME_PLACEHOLDER')}
                        />
                    </label>

                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="BLACKLIST_FIELD_SCOPE" />
                        </span>
                        <select value={form.scope} onChange={(e) => setForm('scope', Number(e.currentTarget.value))}>
                            <For each={SCOPE_VALUES}>{(scope) => <option value={scope}>{scopeLabel(scope)}</option>}</For>
                        </select>
                    </label>

                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="BLACKLIST_FIELD_NOTE" />
                        </span>
                        <input
                            type="text"
                            value={form.note}
                            onInput={(e) => setForm('note', e.currentTarget.value)}
                            placeholder={t('BLACKLIST_FIELD_NOTE_PLACEHOLDER')}
                        />
                    </label>
                </div>

                <div class={styles.toggles}>
                    <label class={styles.toggle}>
                        <input type="checkbox" checked={form.triggerOnSight} onChange={(e) => setForm('triggerOnSight', e.currentTarget.checked)} />
                        <Text val="BLACKLIST_FIELD_ON_SIGHT" />
                    </label>
                    <label class={styles.toggle}>
                        <input type="checkbox" checked={form.triggerOnMessage} onChange={(e) => setForm('triggerOnMessage', e.currentTarget.checked)} />
                        <Text val="BLACKLIST_FIELD_ON_MESSAGE" />
                    </label>
                    <label class={styles.toggle}>
                        <input type="checkbox" checked={form.isEnabled} onChange={(e) => setForm('isEnabled', e.currentTarget.checked)} />
                        <Text val="BLACKLIST_FIELD_ENABLED" />
                    </label>
                </div>

                <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                    <Show when={editingId()}>
                        <button type="button" class="btn btn-secondary" onClick={resetForm}>
                            <Text val="CANCEL" />
                        </button>
                    </Show>
                    <button type="submit" class="btn btn-primary" disabled={submitting() || !form.playerName.trim()}>
                        <Show when={submitting()} fallback={<Text val={editingId() ? 'SAVE' : 'BLACKLIST_ADD'} />}>
                            <i class="fa-solid fa-spinner fa-spin" />
                        </Show>
                    </button>
                </div>
            </form>

            <Show when={loading()}>
                <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show
                    when={(entries() ?? []).length > 0}
                    fallback={<EmptyState icon="fa-user-slash" title={t('BLACKLIST_EMPTY')} hint={t('BLACKLIST_EMPTY_HINT')} />}
                >
                    <div class="fcard">
                        <div class={styles.tableWrap}>
                            <table class={styles.table}>
                                <thead>
                                    <tr>
                                        <th><Text val="BLACKLIST_COL_NAME" /></th>
                                        <th><Text val="BLACKLIST_COL_SCOPE" /></th>
                                        <th><Text val="BLACKLIST_COL_TRIGGERS" /></th>
                                        <th><Text val="BLACKLIST_COL_STATE" /></th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    <For each={entries()}>{(entry) => (
                                        <tr>
                                            <td>
                                                <div class="v" style={{ gap: '2px' }}>
                                                    <span class={styles.name}>{entry.playerName}</span>
                                                    <Show when={entry.note}>
                                                        <span class={styles.note}>{entry.note}</span>
                                                    </Show>
                                                </div>
                                            </td>
                                            <td>{scopeLabel(entry.scope)}</td>
                                            <td>
                                                <div class="h" style={{ gap: 'var(--spacing-sm)', 'flex-wrap': 'wrap' }}>
                                                    <span class={`${styles.pill} ${entry.triggerOnSight ? styles.pillOn : styles.pillOff}`}>
                                                        <Text val="BLACKLIST_FIELD_ON_SIGHT" />
                                                    </span>
                                                    <span class={`${styles.pill} ${entry.triggerOnMessage ? styles.pillOn : styles.pillOff}`}>
                                                        <Text val="BLACKLIST_FIELD_ON_MESSAGE" />
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span class={`${styles.pill} ${entry.isEnabled ? styles.pillOn : styles.pillOff}`}>
                                                    <Text val={entry.isEnabled ? 'BLACKLIST_STATE_ACTIVE' : 'BLACKLIST_STATE_PAUSED'} />
                                                </span>
                                            </td>
                                            <td>
                                                <div class={styles.rowActions}>
                                                    <button class="btn btn-secondary btn-sm" onClick={() => startEdit(entry)}>
                                                        <i class="fa-solid fa-pen-to-square" />
                                                    </button>
                                                    <button class="btn btn-danger btn-sm" onClick={() => void remove(entry)}>
                                                        <i class="fa-solid fa-trash" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}</For>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
